<template>
  <div>
    <SideBar :sidebar-open="sidebarOpen" @update:sidebar-open="(value) => (sidebarOpen = value)" />

    <div class="xl:pl-72">
      <div
        class="sticky top-0 z-40 flex h-16 shrink-0 items-center gap-x-6 border-b border-white/5 bg-background px-4 shadow-sm sm:px-6 lg:px-8"
      >
        <button type="button" id="sidebar" class="-m-2.5 p-2.5 text-white xl:hidden" @click="sidebarOpen = true">
          <span class="sr-only">{{ t("common.openSidebar") }}</span>
          <Bars3Icon class="h-5 w-5" aria-hidden="true" />
        </button>

        <SearchBar :model-value="search" @update:model-value="(text) => (search = text)" />
      </div>

      <AdminHeader />

      <main class="flex flex-col space-y-6 px-12 py-10">
        <template v-if="!isLicensed">
          <div
            class="relative mb-4 w-full rounded-lg border border-destructive/50 p-4 text-destructive dark:border-destructive [&>svg+div]:translate-y-[-3px] [&>svg]:absolute [&>svg]:left-4 [&>svg]:top-4 [&>svg]:text-destructive [&>svg~*]:pl-7"
            role="alert"
          >
            <ExclamationTriangleIcon class="h-4 w-4" />
            <h5 class="mb-1 font-medium leading-none tracking-tight">
              {{ t("err.youDoNotHaveALicense") }}
            </h5>
            <i18n-t class="text-sm [&_p]:leading-relaxed" keypath="err.unlicensedDescription" tag="p">
              <template v-slot:url>
                <a href="https://licensing.knocknoc.io" class="underline hover:no-underline" target="_blank">
                  licensing.knocknoc.io
                </a>
              </template>
            </i18n-t>
          </div>
        </template>
        <template v-else>
          <div class="flex w-full justify-center">
            <KnocStepper
              v-model="wizard.step"
              :steps="[
                {
                  title: t('pages.wizard.stepper.agent.title'),
                  description: t('pages.wizard.stepper.agent.description'),
                  icon: CommandLineIcon,
                  step: 1,
                },
                {
                  title: t('pages.wizard.stepper.settings.title'),
                  description: t('pages.wizard.stepper.settings.description'),
                  icon: Cog8ToothIcon,
                  step: 2,
                },
                {
                  title: t('pages.wizard.stepper.knocoptions.title'),
                  description: t('pages.wizard.stepper.knocoptions.description'),
                  icon: ServerIcon,
                  step: 3,
                },
                {
                  title: t('pages.wizard.stepper.env.title'),
                  description: t('pages.wizard.stepper.env.description'),
                  icon: VariableIcon,
                  step: 4,
                },
                {
                  title: t('pages.wizard.stepper.users.title'),
                  description: t('pages.wizard.stepper.users.description'),
                  icon: UsersIcon,
                  step: 5,
                },
                {
                  title: t('pages.wizard.stepper.review.title'),
                  description: t('pages.wizard.stepper.review.description'),
                  icon: ClipboardIcon,
                  step: 6,
                },
              ]"
            />
          </div>

          <template v-if="wizard.step === 1">
            <AgentSelect
              :options="agents"
              :is-capable="isAgentCapable"
              :value="wizard.agentId"
              :onSelect="wizard.selectAgent"
            />

            <div class="flex justify-end space-x-4">
              <button
                class="inline-flex h-10 items-center justify-center gap-2 whitespace-nowrap rounded-md px-4 py-2 text-sm font-medium text-muted-foreground underline underline-offset-4 ring-offset-background transition-colors hover:no-underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0"
                type="button"
                @click="router.back()"
              >
                {{ t("common.back") }}
              </button>

              <button
                data-testid="next-button"
                class="inline-flex h-10 items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground ring-offset-background transition-colors hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0"
                @click="wizard.changeStep(2)"
                :disabled="!wizard.agentId"
              >
                {{ t("common.next") }}
              </button>
            </div>
          </template>

          <template v-if="wizard.step === 2">
            <KnocForm
              v-model="wizard.form"
              :knoc-options-form="wizard.knocOptionsForm"
              type="SCRIPT"
              :agent="selectedAgent"
              @submit="wizard.changeStep(3)"
              @submit-invalid="globals.focusFirstError"
            >
              <button
                class="inline-flex h-10 items-center justify-center gap-2 whitespace-nowrap rounded-md px-4 py-2 text-sm font-medium text-muted-foreground underline underline-offset-4 ring-offset-background transition-colors hover:no-underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0"
                type="button"
                @click="wizard.changeStep(1)"
              >
                {{ t("common.back") }}
              </button>

              <button
                data-testid="next-button"
                class="inline-flex h-10 items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground ring-offset-background transition-colors hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0"
                type="submit"
              >
                {{ t("common.next") }}
              </button>
            </KnocForm>
          </template>

          <template v-if="wizard.step === 3">
            <KnocOptionsForm
              v-model="wizard.knocOptionsForm"
              :form="wizard.form"
              type="SCRIPT"
              @submit="wizard.changeStep(4)"
              @submit-invalid="globals.focusFirstError"
            >
              <button
                class="inline-flex h-10 items-center justify-center gap-2 whitespace-nowrap rounded-md px-4 py-2 text-sm font-medium text-muted-foreground underline underline-offset-4 ring-offset-background transition-colors hover:no-underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0"
                type="button"
                @click="wizard.changeStep(2)"
              >
                {{ t("common.back") }}
              </button>

              <button
                data-testid="next-button"
                class="inline-flex h-10 items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground ring-offset-background transition-colors hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0"
                type="submit"
              >
                {{ t("common.next") }}
              </button>
            </KnocOptionsForm>
          </template>

          <template v-if="wizard.step === 4">
            <FormKit
              v-model="wizard.envVarsForm"
              id="envVarsForm"
              type="form"
              :actions="false"
              :config="{ validationVisibility: 'submit' }"
              @submit="wizard.changeStep(5)"
              @submit-invalid="globals.focusFirstError"
            >
              <div class="flex flex-col space-y-6">
                <div class="grid grid-cols-2 gap-6">
                  <div class="flex flex-col space-y-6">
                    <h3 class="scroll-m-20 text-2xl font-semibold tracking-tight">
                      {{ t("pages.wizard.steps.env.heading") }}
                    </h3>

                    <FormKit type="list" name="EnvVars" dynamic #default="{ items, node, value }">
                      <div class="flex flex-col">
                        <FormKit v-for="(item, index) in items" type="group" :key="item" :index="index">
                          <div class="flex space-x-4">
                            <FormKit
                              type="text"
                              name="Name"
                              :label="t('pages.wizard.steps.env.fields.Name.label')"
                              :placeholder="t('pages.wizard.steps.env.fields.Name.placeholder')"
                              :validation-messages="envVarValidationMessages"
                              :validation-rules="{
                                checkRepeatedVarNames,
                                mustBeEnteredWithValue: mustBeEnteredAlongside('Value'),
                              }"
                              validation="checkRepeatedVarNames|mustBeEnteredWithValue"
                              :autocomplete="autoComplete"
                            />

                            <FormKit
                              type="text"
                              name="Value"
                              :label="t('pages.wizard.steps.env.fields.Value.label')"
                              :placeholder="t('pages.wizard.steps.env.fields.Value.placeholder')"
                              :validation-messages="envVarValidationMessages"
                              :validation-rules="{ mustBeEnteredWithName: mustBeEnteredAlongside('Name') }"
                              validation="mustBeEnteredWithName"
                              :autocomplete="autoComplete"
                            />

                            <FormKit
                              :classes="{
                                outer: 'mt-[36px]',
                              }"
                              type="checkbox"
                              name="Sensitive"
                              :label="t('pages.wizard.steps.env.fields.Sensitive.label')"
                            />

                            <button
                              class="mt-[26px] inline-flex h-10 w-10 items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0"
                              type="button"
                              @click="() => node.input(value.filter((_, i) => i !== index))"
                              aria-label="Delete envrionment variable"
                            >
                              <TrashIcon class="h-4 w-4 cursor-pointer" />
                            </button>
                          </div>
                        </FormKit>

                        <button
                          data-testid="add-env-var-button"
                          type="button"
                          @click="() => node.input(value.concat({}))"
                          class="mt-2 inline-flex h-9 w-full items-center justify-center gap-2 whitespace-nowrap rounded-md border border-input bg-background px-3 text-sm font-medium ring-offset-background transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0"
                        >
                          {{ t("pages.wizard.steps.env.add") }}
                        </button>
                      </div>
                    </FormKit>

                    <div
                      class="relative col-span-2 w-full rounded-lg border p-4 text-muted-foreground [&>svg+div]:translate-y-[-3px] [&>svg]:absolute [&>svg]:left-4 [&>svg]:top-4 [&>svg]:text-muted-foreground [&>svg~*]:pl-7"
                      role="alert"
                    >
                      <InformationCircleIcon class="h-4 w-4" />
                      <p class="text-sm [&_p]:leading-relaxed">
                        {{ t("pages.wizard.steps.env.informational") }}
                        <code>knoc_action, knoc_aclname, knoc_username, knoc_cidr, knoc_ipaddr</code>
                        <br />{{ t("common.seeDocsForMoreInfo") }}.
                      </p>
                    </div>
                  </div>

                  <div class="space-y-6 rounded-lg border p-6 shadow-sm">
                    <div class="flex items-start justify-between">
                      <h4 class="scroll-m-20 text-xl font-semibold tracking-tight">
                        {{ t("pages.wizard.steps.env.about.heading") }}
                      </h4>

                      <a
                        class="inline-flex items-center gap-1 rounded-md border border-transparent bg-secondary px-2.5 py-0.5 text-xs font-semibold text-secondary-foreground transition-colors hover:bg-secondary/80 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                        href="https://docs.knocknoc.io/"
                        target="_blank"
                        aria-label="Knocknoc documentation (opens in new tab)"
                      >
                        {{ t("common.docs") }}
                        <ArrowTopRightOnSquareIcon class="h-3 w-3" />
                        <span class="sr-only">{{ t("common.opensInNewTab") }}</span>
                      </a>
                    </div>

                    <div class="space-y-4 text-sm text-muted-foreground">
                      <p>
                        {{ t("pages.wizard.steps.env.about.description") }}
                      </p>
                      <ul class="list-disc space-y-2 pl-5">
                        <li>
                          {{ t("pages.wizard.steps.env.about.dotPoints.0") }}
                        </li>
                        <li>
                          {{ t("pages.wizard.steps.env.about.dotPoints.1") }}
                        </li>
                        <li>
                          {{ t("pages.wizard.steps.env.about.dotPoints.2") }}
                        </li>
                      </ul>
                      <p>
                        {{ t("pages.wizard.steps.env.about.sensitiveDescription") }}
                      </p>
                      <p>
                        {{ t("pages.wizard.steps.env.about.sensitiveAdditional") }}
                        <a
                          class="underline hover:no-underline"
                          href="https://docs.knocknoc.io/"
                          target="_blank"
                          aria-label="Knocknoc documentation (opens in new tab)"
                          >{{ t("common.here").toLowerCase() }} </a
                        >.
                      </p>
                    </div>
                  </div>
                </div>

                <div class="flex justify-end space-x-4">
                  <button
                    class="inline-flex h-10 items-center justify-center gap-2 whitespace-nowrap rounded-md px-4 py-2 text-sm font-medium text-muted-foreground underline underline-offset-4 ring-offset-background transition-colors hover:no-underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0"
                    type="button"
                    @click="wizard.changeStep(3)"
                  >
                    {{ t("common.back") }}
                  </button>

                  <button
                    data-testid="next-button"
                    class="inline-flex h-10 items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground ring-offset-background transition-colors hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0"
                    type="submit"
                  >
                    {{ t("common.next") }}
                  </button>
                </div>
              </div>
            </FormKit>
          </template>

          <template v-if="wizard.step === 5">
            <GroupSelect :onSelect="wizard.selectGroup" :options="groups" :value="wizard.groupIds" />
            <UserSelect
              :options="users.filter((user) => user.AuthType !== 'SAML')"
              :value="wizard.userIds"
              :onSelect="wizard.selectUser"
              :onCreate="handleCreateUser"
            />

            <div class="flex justify-end space-x-4">
              <button
                class="inline-flex h-10 items-center justify-center gap-2 whitespace-nowrap rounded-md px-4 py-2 text-sm font-medium text-muted-foreground underline underline-offset-4 ring-offset-background transition-colors hover:no-underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0"
                type="button"
                @click="wizard.changeStep(4)"
              >
                {{ t("common.back") }}
              </button>

              <button
                data-testid="next-button"
                class="inline-flex h-10 items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground ring-offset-background transition-colors hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0"
                @click="wizard.changeStep(6)"
              >
                {{ t("common.next") }}
              </button>
            </div>
          </template>

          <template v-if="wizard.step === 6">
            <div class="grid grid-cols-2 gap-6">
              <div class="flex flex-col space-y-6">
                <div
                  data-testid="settings-card"
                  class="flex flex-col space-y-6 rounded-lg border bg-background p-6 shadow-sm transition-colors"
                >
                  <h3 class="scroll-m-20 text-2xl font-semibold tracking-tight">
                    {{ t("pages.wizard.steps.settings.heading") }}
                  </h3>

                  <div class="grid w-full grid-cols-2 gap-4">
                    <div>
                      <h4 class="text-sm font-medium text-muted-foreground">
                        {{ t("pages.wizard.steps.settings.fields.BackendConfig.script.Command.label") }}
                      </h4>
                      <p class="truncate">
                        {{ wizard.form.BackendConfig.script.Command }}
                      </p>
                    </div>
                    <div>
                      <h4 class="text-sm font-medium text-muted-foreground">
                        {{ t("pages.wizard.steps.settings.fields.ACLConfig.fields.script.fields.Name.label") }}
                      </h4>
                      <p class="truncate">
                        {{ wizard.form.ACLConfig.script.Name || "-" }}
                      </p>
                    </div>
                    <div>
                      <h4 class="text-sm font-medium text-muted-foreground">
                        {{ t("pages.wizard.steps.settings.fields.Description.label") }}
                      </h4>
                      <p class="truncate">
                        {{ wizard.form.Description }}
                      </p>
                    </div>
                    <div>
                      <h4 class="text-sm font-medium text-muted-foreground">
                        {{ t("pages.wizard.steps.settings.fields.URL.label") }}
                      </h4>
                      <p class="truncate">
                        {{ wizard.form.URL || "-" }}
                      </p>
                    </div>
                  </div>
                </div>

                <AssignmentsPreview :users="selectedUsers" :groups="selectedGroups" />
              </div>

              <div class="flex flex-col space-y-6">
                <div
                  data-testid="knoc-options-card"
                  class="flex flex-col space-y-6 rounded-lg border bg-background p-6 shadow-sm transition-colors"
                >
                  <h3 class="scroll-m-20 text-2xl font-semibold tracking-tight">
                    {{ t("pages.wizard.steps.knocOptions.reviewTitle") }}
                  </h3>

                  <div class="grid w-full grid-cols-2 gap-4">
                    <div>
                      <h4 class="text-sm font-medium text-muted-foreground">
                        {{ t("pages.wizard.steps.settings.fields.MaximumGrantDuration.label") }}
                      </h4>
                      <p class="truncate">
                        <span v-if="wizard.knocOptionsForm.MaximumGrantDuration">
                          {{ wizard.knocOptionsForm.MaximumGrantDuration }}
                        </span>
                        <span v-else>-</span>
                      </p>
                    </div>
                    <div v-if="wizard.knocOptionsForm.GrantType.Type === 'userIp'">
                      <h4 class="text-sm font-medium text-muted-foreground">
                        {{ t("pages.wizard.steps.settings.fields.GrantType.heading") }}
                      </h4>
                      <p class="truncate">{{ t("pages.wizard.steps.settings.fields.GrantType.summary.usersIp") }}</p>
                    </div>
                  </div>

                  <div class="col-span-2 flex flex-col space-y-4">
                    <div v-if="wizard.knocOptionsForm.ClickToGrant" class="inline-flex items-center">
                      <span
                        :class="
                          twMerge(
                            'formkit-decorator relative block aspect-[1/1] w-[1em] select-none rounded border border-neutral-400 bg-white text-lg text-transparent shadow ring-blue-500 group-[]/multistep:shadow-none group-[]/repeater:shadow-none peer-focus-visible:ring-2 peer-focus-visible:ring-offset-1 dark:border-neutral-500 dark:bg-transparent dark:ring-offset-blue-500 dark:peer-focus-visible:ring-1 dark:peer-disabled:bg-neutral-600/50',
                            wizard.knocOptionsForm.ClickToGrant &&
                              'border-blue-600 bg-blue-50 text-blue-600 dark:bg-blue-900 dark:text-blue-400',
                          )
                        "
                      />
                      <span class="ml-2">
                        {{ t("pages.wizard.steps.settings.fields.ClickToGrant.label") }}
                      </span>
                    </div>
                    <div v-if="wizard.form.CGNATOpen" class="inline-flex items-center">
                      <span
                        :class="
                          twMerge(
                            'formkit-decorator relative block aspect-[1/1] w-[1em] select-none rounded border border-neutral-400 bg-white text-lg text-transparent shadow ring-blue-500 group-[]/multistep:shadow-none group-[]/repeater:shadow-none peer-focus-visible:ring-2 peer-focus-visible:ring-offset-1 dark:border-neutral-500 dark:bg-transparent dark:ring-offset-blue-500 dark:peer-focus-visible:ring-1 dark:peer-disabled:bg-neutral-600/50',
                            wizard.form.CGNATOpen &&
                              'border-blue-600 bg-blue-50 text-blue-600 dark:bg-blue-900 dark:text-blue-400',
                          )
                        "
                      />
                      <span class="ml-2">
                        {{ t("pages.wizard.steps.settings.fields.CGNATOpen.label") }}
                      </span>
                    </div>
                    <div v-if="wizard.knocOptionsForm.HideFromUsers" class="inline-flex items-center">
                      <span
                        :class="
                          twMerge(
                            'formkit-decorator relative block aspect-[1/1] w-[1em] select-none rounded border border-neutral-400 bg-white text-lg text-transparent shadow ring-blue-500 group-[]/multistep:shadow-none group-[]/repeater:shadow-none peer-focus-visible:ring-2 peer-focus-visible:ring-offset-1 dark:border-neutral-500 dark:bg-transparent dark:ring-offset-blue-500 dark:peer-focus-visible:ring-1 dark:peer-disabled:bg-neutral-600/50',
                            wizard.knocOptionsForm.HideFromUsers &&
                              'border-blue-600 bg-blue-50 text-blue-600 dark:bg-blue-900 dark:text-blue-400',
                          )
                        "
                      />
                      <span class="ml-2">
                        {{ t("pages.wizard.steps.settings.fields.HideFromUsers.label") }}
                      </span>
                    </div>
                    <div v-if="wizard.knocOptionsForm.AutoBrowseUsers" class="inline-flex items-center">
                      <span
                        :class="
                          twMerge(
                            'formkit-decorator relative block aspect-[1/1] w-[1em] select-none rounded border border-neutral-400 bg-white text-lg text-transparent shadow ring-blue-500 group-[]/multistep:shadow-none group-[]/repeater:shadow-none peer-focus-visible:ring-2 peer-focus-visible:ring-offset-1 dark:border-neutral-500 dark:bg-transparent dark:ring-offset-blue-500 dark:peer-focus-visible:ring-1 dark:peer-disabled:bg-neutral-600/50',
                            wizard.knocOptionsForm.AutoBrowseUsers &&
                              'border-blue-600 bg-blue-50 text-blue-600 dark:bg-blue-900 dark:text-blue-400',
                          )
                        "
                      />
                      <span class="ml-2">
                        {{ t("pages.wizard.steps.settings.fields.AutoBrowseUsers.label") }}
                      </span>
                    </div>
                  </div>

                  <div
                    v-if="wizard.knocOptionsForm.GrantType.Type !== 'userIp'"
                    class="col-span-2 flex flex-col space-y-4"
                  >
                    <h3 class="scroll-m-20 text-2xl font-semibold tracking-tight">
                      {{ t("pages.wizard.steps.settings.fields.GrantType.heading") }}
                    </h3>

                    <div class="col-span-2">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>
                              {{ t("pages.wizard.steps.settings.fields.GrantType.summary.table.heading1") }}
                            </TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          <TableRow v-for="{ Ip } in wizard.knocOptionsForm.GrantType.Ips" :key="Ip">
                            <TableCell>
                              {{ Ip }}
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </div>
                  </div>

                  <div class="w-full space-y-4">
                    <h4 class="scroll-m-20 text-xl font-semibold tracking-tight">
                      {{ t("pages.wizard.steps.settings.fields.IpRestrictions.heading") }}
                      <span v-if="wizard.knocOptionsForm.IpExclusions.RestrictionType !== 'none'"> </span>
                    </h4>
                    <h4 class="text-sm font-medium text-muted-foreground">
                      {{
                        (wizard.knocOptionsForm.IpExclusions.RfcType
                          ? t(
                              `pages.wizard.steps.settings.fields.IpRestrictions.rfc.options.${wizard.knocOptionsForm.IpExclusions.RfcType}`,
                            ) + " "
                          : "") +
                        t(
                          `pages.wizard.steps.settings.fields.IpRestrictions.options.${wizard.knocOptionsForm.IpExclusions.RestrictionType}`,
                        )
                      }}
                    </h4>

                    <div class="col-span-2" v-if="wizard.knocOptionsForm.IpExclusions.RestrictionType !== 'none'">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>
                              {{ t("pages.wizard.steps.settings.fields.IpRestrictions.fields.start.label") }}
                            </TableHead>
                            <TableHead>
                              {{ t("pages.wizard.steps.settings.fields.IpRestrictions.fields.end.label") }}
                            </TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          <TableRow v-for="{ Start, End } in wizard.knocOptionsForm.IpExclusions.Ranges" :key="Start">
                            <TableCell>
                              {{ Start }}
                            </TableCell>
                            <TableCell>
                              {{ End }}
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                </div>

                <div
                  data-testid="environment-variables-card"
                  class="flex flex-col space-y-6 rounded-lg border bg-background p-6 shadow-sm transition-colors"
                >
                  <h3 class="scroll-m-20 text-2xl font-semibold tracking-tight">Environment Variables</h3>

                  <div
                    v-if="wizard.envVarsForm.EnvVars.filter((envVar) => envVar.Name !== '').length === 0"
                    class="flex justify-center rounded-lg border p-3 text-muted-foreground shadow-sm"
                  >
                    No environment variables defined.
                  </div>
                  <div v-else class="flex flex-col space-y-3">
                    <div
                      v-for="envVar in wizard.envVarsForm.EnvVars.filter((envVar) => envVar.Name !== '')"
                      class="flex flex-col space-y-1 rounded-md border p-3"
                    >
                      <div class="flex items-center">
                        <span class="font-mono text-sm font-medium">{{ envVar.Name }}</span>
                        <div
                          v-if="envVar.Sensitive"
                          class="ml-2 inline-flex items-center rounded-full border border-amber-600 bg-amber-950/30 px-2.5 py-0.5 text-xs font-semibold text-amber-400 transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                        >
                          Sensitive
                        </div>
                      </div>

                      <span v-if="envVar.Sensitive" class="font-mono text-sm text-gray-400">
                        {{ envVar.Value.replace(/./g, "*") }}
                      </span>
                      <span v-else class="font-mono text-sm text-gray-400">
                        {{ envVar.Value }}
                      </span>
                    </div>
                  </div>
                </div>

                <AgentsPreview :agent="selectedAgent" :get-warnings="getAgentWarnings" />
              </div>
            </div>

            <div class="flex justify-end space-x-4">
              <button
                class="inline-flex h-10 items-center justify-center gap-2 whitespace-nowrap rounded-md px-4 py-2 text-sm font-medium text-muted-foreground underline underline-offset-4 ring-offset-background transition-colors hover:no-underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0"
                type="button"
                @click="wizard.changeStep(5)"
              >
                {{ t("common.back") }}
              </button>

              <button
                data-testid="submit-button"
                class="inline-flex h-10 items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground ring-offset-background transition-colors hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0"
                @click="handleSubmit"
              >
                {{ t("common.submit") }}
              </button>
            </div>
          </template>
        </template>
      </main>
    </div>
  </div>

  <ErrorNotification
    :error="errorMessage"
    :title="t('common.error')"
    @update:error="(input) => (errorMessage = input)"
  />
</template>

<script setup>
import { Bars3Icon } from "@heroicons/vue/24/solid";
import {
  ArrowTopRightOnSquareIcon,
  ClipboardIcon,
  Cog8ToothIcon,
  CommandLineIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  ServerIcon,
  TrashIcon,
  UsersIcon,
  VariableIcon,
} from "@heroicons/vue/24/outline";
import { twMerge } from "tailwind-merge";
import { computed, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";

import globals from "../../globals";
import { createKnoc } from "../../utils";
import { isAgentCapableForScript, getVersionWarnings } from "../../utils/agentCapabilities";

import { useWizardStore } from "../../pinia/wizard";

import SearchBar from "../../components/SearchBar.vue";
import SideBar from "../../components/SideBar.vue";
import ErrorNotification from "../../components/ErrorNotification.vue";
import AgentSelect from "../../components/AgentSelect.vue";
import UserSelect from "../../components/UserSelect.vue";
import GroupSelect from "../../components/GroupSelect.vue";
import KnocForm from "../../components/KnocForm.vue";
import KnocStepper from "../../components/KnocStepper.vue";
import AgentsPreview from "../../components/AgentsPreview.vue";
import AssignmentsPreview from "../../components/AssignmentsPreview.vue";
import KnocOptionsForm from "../../components/KnocOptionsForm.vue";
import TableBody from "../../components/ui/table/TableBody.vue";
import TableHead from "../../components/ui/table/TableHead.vue";
import Table from "../../components/ui/table/Table.vue";
import TableHeader from "../../components/ui/table/TableHeader.vue";
import TableCell from "../../components/ui/table/TableCell.vue";
import TableRow from "../../components/ui/table/TableRow.vue";
import AdminHeader from "../../components/AdminHeader.vue";
import { useAdminStore } from "../../pinia/admin.ts";
import { useUserStore } from "../../pinia/user.ts";

const { t } = useI18n();
const router = useRouter();

const adminStore = useAdminStore();
adminStore.fetchStatus();

const userStore = useUserStore();

const isLicensed = computed(() => adminStore.user?.Licensing?.Licensed);

const sidebarOpen = ref(false);
const search = ref("");

const errorMessage = ref(null);

const wizard = useWizardStore();

const autoComplete = import.meta.env?.VITE_AUTOCOMPLETE || "off";

//region Agent
const agents = ref([]);
function isAgentCapable(agent) {
  return isAgentCapableForScript(agent);
}
function getAgentWarnings(agent) {
  return getVersionWarnings(agent, isAgentCapable);
}
const selectedAgent = computed(() => {
  return agents.value.find((agent) => agent.ID === wizard.agentId);
});
//endregion

//region Env
const envVarValidationMessages = {
  checkRepeatedVarNames: t("err.repeatedEnvVarNames"),
};

function checkRepeatedVarNames(node) {
  const vars = Object.values(wizard.envVarsForm.EnvVars);
  return vars.filter((n) => n.Name === node.value).length <= 1;
}

function mustBeEnteredAlongside(siblingNodeName) {
  return function (node) {
    return Boolean(node.value) === Boolean(node.parent.value[siblingNodeName]);
  };
}
//endregion

//region Users
const users = ref([]);
const selectedUsers = computed(() => {
  return users.value.filter((user) => wizard.userIds.includes(user.ID));
});
async function handleCreateUser([_, err]) {
  if (err) {
    errorMessage.value = globals.renderError(t, err);
    return;
  }

  fetchUsers();
}

const groups = ref([]);
const selectedGroups = computed(() => {
  return groups.value.filter((group) => wizard.groupIds.includes(group.ID));
});
//endregion

async function handleSubmit() {
  try {
    const [knocId] = await createKnoc({
      type: "SCRIPT",
      ...wizard,
      users: selectedUsers.value,
      groups: selectedGroups.value,
    });

    wizard.$reset();
    router.push({ path: "/admin/knocs", query: { viewKnocId: knocId } });
  } catch (err) {
    errorMessage.value = globals.renderError(t, err);
  }
}

function fetchAgents() {
  userStore.fetchAgents({ force: true }).then(
    (response) => {
      agents.value = response.data;
    },
    (error) => {
      error.value = globals.renderError(t, error);
    },
  );
}

function fetchUsers() {
  userStore.fetchUsers({ force: true }).then(
    (response) => {
      users.value = response.data;
    },
    (error) => {
      error.value = globals.renderError(t, error);
    },
  );
}

function fetchGroups() {
  userStore.fetchGroups({ force: true }).then(
    (response) => {
      groups.value = response.data;
    },
    (error) => {
      error.value = globals.renderError(t, error);
    },
  );
}

onMounted(() => {
  fetchAgents();
  fetchUsers();
  fetchGroups();
});
</script>
